/**
 * The app's icon system — inline Feather/Lucide-style SVG.
 *
 * DESIGN.md ("Shapes") is explicit: *"Icons are inline **Feather/Lucide-style SVG** (stroke
 * `currentColor`, 2px), 13–18px; **no emoji, no icon font**."* Until this module existed the
 * app had zero inline SVG and exactly one glyph — a `🔔` emoji — which is the thing the spec
 * bans, for three reasons worth writing down because they are invisible until you hit them:
 *
 *   1. **Emoji ignore `color`.** A colour-font glyph paints its own palette, so the bell could
 *      not follow `--text-2` in light mode and `--text-2`'s dark variant in dark mode. Every
 *      other control in the topbar shifts with the theme; the emoji did not.
 *   2. **Emoji are platform art.** The same codepoint is a different drawing on macOS, Windows,
 *      Android and each Linux font stack, so the one place the design is least under our
 *      control was the only place a picture appeared.
 *   3. **An icon font would be worse still** — a second network asset, a flash of missing
 *      glyphs, and a rendering that dies with the font.
 *
 * Inline SVG has none of those failure modes: `stroke="currentColor"` means one asset is
 * correct on the light topbar AND on the navy sidebar (which stays navy in BOTH themes), with
 * no theme-swapped pair and no `data-theme` rules of its own.
 *
 * **Paths are lifted from the prototype** (`design/Maritime Vendor Compliance.dc.html`) where
 * it drew one, and from the Feather/Lucide 24×24 sets otherwise. Keeping the 24×24 viewBox
 * while scaling via width/height is what makes `stroke-width: 2` render at a consistent
 * optical weight across every size we use.
 */

/**
 * The icon set. A closed union rather than a `string`, so a typo is a compile error at the
 * call site instead of an invisible empty square, and so the registry stays enumerable for
 * the completeness test.
 *
 * Grouped by where they are used: chrome controls first, then one per sidebar nav slot
 * across the three shells (`CockpitShell`, `AdminShell`, `PortalShell`).
 */
export type IconName =
  // Topbar / overlay chrome
  | 'bell'
  | 'sun'
  | 'moon'
  | 'settings'
  | 'close'
  | 'log-out'
  // Sidebar nav
  | 'layout'
  | 'ship'
  | 'package'
  | 'cpu'
  | 'upload'
  | 'shield-check'
  | 'book'
  | 'file-text'
  | 'users'
  | 'user'
  | 'user-check'
  | 'building'
  | 'clock'
  | 'check-circle'
  | 'inbox'
  | 'award'
  // Device-category glyphs (one per DEVICE_CATEGORIES value). `cpu` doubles as
  // Automation and `package` as Other, so only five new shapes were needed.
  | 'compass'
  | 'radio'
  | 'zap'
  | 'life-buoy'
  | 'waves';

/**
 * Name → geometry. `Record<IconName, …>` (not a partial) makes an unregistered name a type
 * error, so the union above and this table can never drift apart.
 *
 * Everything here is stroke-only: no `fill`, no hard-coded colour. The `<svg>` wrapper below
 * owns `fill="none"`, `stroke="currentColor"` and the stroke weight/joins, so an entry cannot
 * accidentally opt out of the contract by forgetting them.
 */
const GEOMETRY: Record<IconName, React.ReactNode> = {
  bell: (
    <>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>
  ),
  moon: <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />,
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
  close: (
    <>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </>
  ),
  'log-out': (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
    </>
  ),
  layout: (
    <>
      <rect x="3" y="3" width="7" height="9" />
      <rect x="14" y="3" width="7" height="5" />
      <rect x="14" y="12" width="7" height="9" />
      <rect x="3" y="16" width="7" height="5" />
    </>
  ),
  ship: (
    <>
      <path d="M3 18l2-7h14l2 7" />
      <path d="M12 4v7" />
      <path d="M8 7h8" />
      <path d="M2 18a3 3 0 0 0 4 0 3 3 0 0 1 4 0 3 3 0 0 0 4 0 3 3 0 0 1 4 0" />
    </>
  ),
  package: (
    <>
      <path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="M3.3 7L12 12l8.7-5" />
      <path d="M12 22V12" />
    </>
  ),
  cpu: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <rect x="9" y="9" width="6" height="6" />
      <path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3" />
    </>
  ),
  upload: (
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M17 8l-5-5-5 5" />
      <path d="M12 3v12" />
    </>
  ),
  'shield-check': (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  book: (
    <>
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </>
  ),
  'file-text': (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M16 13H8M16 17H8M10 9H8" />
    </>
  ),
  users: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  user: (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  ),
  'user-check': (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <path d="M17 11l2 2 4-4" />
    </>
  ),
  building: (
    <>
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-2" />
      <path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  'check-circle': (
    <>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <path d="M22 4 12 14.01l-3-3" />
    </>
  ),
  inbox: (
    <>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </>
  ),
  award: (
    <>
      <circle cx="12" cy="8" r="6" />
      <path d="M15.5 12.9l1 8.1-4.5-2.7L7.5 21l1-8.1" />
    </>
  ),

  // --- Device categories ---
  // Navigation: a compass rose — the needle is a polygon, which the `fill="none"` wrapper
  // renders as an outline, so it reads as a needle rather than a filled arrow.
  compass: (
    <>
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </>
  ),
  // Communication: a transmitter — a point source with two pairs of radiating arcs.
  radio: (
    <>
      <circle cx="12" cy="12" r="2" />
      <path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14" />
    </>
  ),
  // Propulsion: power delivered — a bolt, not a literal propeller, which loses its blades
  // to the 2px stroke at 16px and turns into a grey disc.
  zap: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />,
  // Safety: the life ring. The one glyph in this group with an unambiguous maritime reading.
  'life-buoy': (
    <>
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="4" />
      <line x1="4.93" y1="4.93" x2="9.17" y2="9.17" />
      <line x1="14.83" y1="9.17" x2="19.07" y2="4.93" />
      <line x1="14.83" y1="14.83" x2="19.07" y2="19.07" />
      <line x1="4.93" y1="19.07" x2="9.17" y2="14.83" />
    </>
  ),
  // Sonar: stacked wavefronts. Distinct from `radio`'s arcs on purpose — the two categories
  // are adjacent in the list and must not resolve to the same silhouette at 16px.
  waves: (
    <>
      <path d="M2 7c2-2 4-2 6 0s4 2 6 0 4-2 6 0" />
      <path d="M2 12c2-2 4-2 6 0s4 2 6 0 4-2 6 0" />
      <path d="M2 17c2-2 4-2 6 0s4 2 6 0 4-2 6 0" />
    </>
  ),
};

/** Every registered name, in declaration order — the completeness test iterates this. */
export const ICON_NAMES = Object.keys(GEOMETRY) as IconName[];

/**
 * Render one icon.
 *
 * **`aria-hidden` by default**, for the same reason `Avatar` is: every icon in this app either
 * sits beside its own text label (sidebar nav, "Sign out") or inside a button that already
 * carries an `aria-label` (theme toggle, bell, settings, close). Exposing it as well would make
 * a screen reader announce the control twice. Pass `label` ONLY for a genuinely standalone
 * icon — and note that an icon-only *button* does not qualify: label the button, not the glyph,
 * so the accessible name lives on the thing that is actually focusable and clickable.
 *
 * `size` defaults to 16px, the middle of DESIGN.md's 13–18px range. It sets width/height
 * attributes rather than a CSS class because the size varies per call site (17px in the
 * sidebar, 18px in a 38px icon button) and is a property of the instance, not of a component
 * state — unlike, say, the tone of a status pill, which belongs in CSS.
 */
export function Icon({
  name,
  size = 16,
  label,
  className,
}: {
  name: IconName;
  /** Rendered edge length in px. DESIGN.md's range is 13–18. */
  size?: number;
  /** Accessible name. Omit whenever adjacent text or the wrapping button already names it. */
  label?: string;
  className?: string;
}): React.JSX.Element {
  return (
    <svg
      className={className === undefined ? 'icon' : `icon ${className}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      // Keeps the SVG out of the tab order in browsers that make one focusable by default;
      // an icon is never the interactive element, the button around it is.
      focusable="false"
      {...(label === undefined ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label })}
    >
      {GEOMETRY[name]}
    </svg>
  );
}
